import { Badge } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  ExperimentOutlined,
  CameraOutlined,
  OrderedListOutlined,
  PictureOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  WalletOutlined,
  AppstoreAddOutlined,
  SkinOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import logo from '../../assets/logo.jpg';
import { useTaskStore } from '../../stores/useTaskStore';
import { useAuth } from '../../auth/AuthContext';
import { isStaffRole } from '../../auth/roles';

interface SidebarProps {
  variant?: 'desktop' | 'drawer';
  onNavigate?: () => void;
}

export function Sidebar({ variant = 'desktop', onNavigate }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const tasks = useTaskStore((state) => state.tasks);
  const { user } = useAuth();
  const navigationGroups = createNavigationGroups(Boolean(window.CLOUD_RUNTIME), user?.featureKeys || []);
  const activeTaskCount = useMemo(
    () => tasks.filter((task) => ['pending', 'validating', 'running'].includes(task.status)).length,
    [tasks]
  );

  const goTo = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <aside className={`app-sidebar ${variant === 'drawer' ? 'is-drawer' : 'is-desktop'}`}>
      <div className="brand-lockup">
        <div className="brand-mark">
          <img src={logo} alt="" />
        </div>
        <div className="min-w-0">
          <div className="brand-title">AI Fashion</div>
          <div className="brand-subtitle">Studio Workspace</div>
        </div>
      </div>

      <nav className="sidebar-nav" aria-label="主导航">
        {navigationGroups.map((group) => (
          <div className="nav-group" key={group.label}>
            <div className="nav-group-label">{group.label}</div>
            {group.items.map((item) => {
              const isActive = location.pathname === item.key;
              const showTaskBadge = item.key === '/tasks' && activeTaskCount > 0;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`nav-item ${isActive ? 'is-active' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => goTo(item.key)}
                >
                  <span className="nav-item-icon">{item.icon}</span>
                  <span className="nav-item-label">{item.label}</span>
                  {showTaskBadge && (
                    <Badge
                      count={activeTaskCount}
                      overflowCount={99}
                      size="small"
                      className="nav-item-badge"
                    />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        {window.CLOUD_RUNTIME && isStaffRole(user?.role) && (
          <button type="button" className={`nav-item ${location.pathname === '/admin' ? 'is-active' : ''}`} onClick={() => goTo('/admin')}>
            <span className="nav-item-icon"><SettingOutlined /></span><span className="nav-item-label">管理中心</span>
          </button>
        )}
        {window.CLOUD_RUNTIME && (
          <button type="button" className={`nav-item ${location.pathname === '/account' ? 'is-active' : ''}`} onClick={() => goTo('/account')}>
            <span className="nav-item-icon"><WalletOutlined /></span>
            <span className="nav-item-label">{user?.username}</span>
            <span className="nav-account-points">{user?.points ?? 0} 点</span>
          </button>
        )}
        <button
          type="button"
          className={`nav-item ${location.pathname === '/about' ? 'is-active' : ''}`}
          onClick={() => goTo('/about')}
        >
          <span className="nav-item-icon"><InfoCircleOutlined /></span>
          <span className="nav-item-label">关于</span>
        </button>
        <div className="system-status">
          <span className={`status-dot ${activeTaskCount > 0 ? 'is-busy' : ''}`} />
          <span>{activeTaskCount > 0 ? `${activeTaskCount} 个任务处理中` : '系统就绪'}</span>
        </div>
      </div>
    </aside>
  );
}

function createNavigationGroups(cloudRuntime: boolean, featureKeys: string[]) {
  const canUse = (key: string) => !cloudRuntime || featureKeys.includes(key);
  return [
    {
      label: '工作空间',
      items: [
        { key: '/', icon: <DashboardOutlined />, label: '概览' },
        ...(canUse('tryon') ? [{ key: '/tryon', icon: <ExperimentOutlined />, label: 'AI 试衣' }] : []),
        ...(cloudRuntime && canUse('fabric-tryon') ? [{ key: '/fabric-tryon', icon: <SkinOutlined />, label: '服装换面料' }] : []),
        ...(cloudRuntime && canUse('fabric-display') ? [{ key: '/fabric-display', icon: <BgColorsOutlined />, label: '面料展示工作台' }] : []),
        ...(canUse('garment-display') ? [{ key: '/showcase', icon: <CameraOutlined />, label: '服装展示' }] : []),
      ],
    },
    {
      label: '资源中心',
      items: [
        { key: '/models', icon: <UserOutlined />, label: '模特库' },
        ...(cloudRuntime ? [
          { key: '/submissions', icon: <AppstoreAddOutlined />, label: '资源共建' },
        ] : []),
      ],
    },
    {
      label: '生产管理',
      items: [
        { key: '/tasks', icon: <OrderedListOutlined />, label: '任务队列' },
        { key: '/results', icon: <PictureOutlined />, label: '结果管理' },
      ],
    },
  ];
}
